import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { campusApi } from "./campus-api";
import { Campus, CreateCampusDto } from "./types";
import CampusFormModal from "./components/CampusFormModal";

export default function CampusManagement() {
  const navigate = useNavigate();
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCampus, setEditingCampus] = useState<Campus | null>(null);

  useEffect(() => {
    fetchCampuses();
  }, []);

  const fetchCampuses = async () => {
    setLoading(true);
    try {
      const data = await campusApi.getAllCampuses();
      setCampuses(data);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to load campuses");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCampus(null);
    setModalOpen(true);
  };

  const handleEdit = (campus: Campus) => {
    setEditingCampus(campus);
    setModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this campus? All associated nodes and cameras will also be deleted.")) {
      return;
    }

    try {
      await campusApi.deleteCampus(id);
      toast.success("Campus deleted successfully");
      fetchCampuses();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to delete campus");
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingCampus(null);
    fetchCampuses();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Campus Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Manage campuses and their associated nodes and cameras
              </p>
            </div>
            <button
              onClick={handleCreate}
              className="btn-primary px-4 py-2 flex items-center space-x-2"
            >
              <i className="fa-solid fa-plus"></i>
              <span>Add Campus</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : campuses.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No campuses found. Click "Add Campus" to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {campuses.map((campus) => (
                    <tr
                      key={campus.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {campus.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {campus.location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {campus.createdAt ? new Date(campus.createdAt).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/campuses/${campus.id}/nodes`)}
                            className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 transition-colors"
                            title="View nodes"
                          >
                            <i className="fa-solid fa-network-wired"></i>
                          </button>
                          <button
                            onClick={() => handleEdit(campus)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                            title="Edit campus"
                          >
                            <i className="fa-solid fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(campus.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                            title="Delete campus"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <CampusFormModal
        open={modalOpen}
        onClose={handleModalClose}
        campus={editingCampus}
      />
    </div>
  );
}

